import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { DataLayer } from "./types/DataLayer.types";
import { PageLoad } from "./types/events/PageLoad.types";
import { store } from "../../redux/store";

declare global {
  interface Window {
    adobeDataLayer: DataLayer[];
  }
}

export const useAdobe = () => {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    window.adobeDataLayer = window.adobeDataLayer || [];
  }, []);

  //UTILS
  const loadAdobeScript = useCallback((env: "test" | "prep" | "prod") => {
    let adobeScript = document.createElement("script");
    if (env === "test" || env === "prep") {
      adobeScript.src =
        "https://assets.adobedtm.com/46c0f31671a9/0bfbd63a254c/launch-69364b8ce472-staging.min.js";
    } else {
      if (env === "prod") {
        adobeScript.src =
          "https://assets.adobedtm.com/46c0f31671a9/0bfbd63a254c/launch-b92494afdd6c.min.js";
      }
    }
    document.getElementsByTagName("head")[0].appendChild(adobeScript);
  }, []);

  const pushEvent = useCallback((dl: DataLayer) => {
    window.adobeDataLayer.push(dl);
  }, []);

  const defineProcessName = useCallback(() => {
    let acquisitionpage = store.getState()?.queryParams.acquisitionpage;
    if (acquisitionpage === "iCornerApp") return "3pil-crossselling";
    return "3pil-full";
  }, []);

  const defineStepName = useCallback(() => {
    return location.pathname.toLowerCase().replace("/", "");
  }, [location.pathname]);

  const defineSection_1 = useCallback((): "3pil" => {
    return "3pil";
  }, []);

  const defineSection_2 = useCallback((): "full" | "crossselling" => {
    let acquisitionpage = store.getState()?.queryParams.acquisitionpage;
    if (acquisitionpage === "iCornerApp") return "crossselling";
    return "full";
  }, []);

  const definePageName = useCallback(() => {
    return defineSection_1() + ":" + defineSection_2() + ":" + defineStepName();
  }, [defineSection_1, defineSection_2, defineStepName]);

  const defineAcquisitionPage = useCallback((): "LP" | "PP" | "iCornerApp" => {
    let acquisitionpage = store.getState()?.queryParams.acquisitionpage;
    if (acquisitionpage === "iCornerApp") return acquisitionpage;
    return document?.referrer?.includes("landing") ? "LP" : "PP";
  }, []);

  const defineMedium = useCallback((): string => {
    let medium = store.getState()?.queryParams.medium;
    if (medium) return medium.toLowerCase();
    return "";
  }, []);

  const defineInternalCampaign = useCallback((): string => {
    let intcmp = store.getState()?.queryParams.intcmp;
    if (intcmp) return intcmp.toLowerCase();
    return "";
  }, []);

  const defineCid = useCallback((): string => {
    let cid = store.getState()?.queryParams.cid;
    if (cid) return cid.toLowerCase();
    return "";
  }, []);

  const defineWave = useCallback((): string => {
    let wave = store.getState()?.queryParams.wave;
    if (wave) return wave;
    return "";
  }, []);

  const defineCampaignCode = useCallback((): string => {
    let campaigncode = store.getState()?.queryParams.campaignCode;
    if (campaigncode) return campaigncode;
    return "";
  }, []);

  //EVENTS
  const firePageLoad = useCallback(() => {
    let dataLayer: PageLoad = {
      //Page
      page: {
        language: i18n.resolvedLanguage ?? "en",
        pagename: definePageName(),
        currentpagename: defineStepName(),
        section1: defineSection_1(),
        section2: defineSection_2(),
        acquisitionpage: defineAcquisitionPage(),
        intcmp: defineInternalCampaign(),
        medium: defineMedium(),
      },
      //Product
      product: {
        sourcecode: "",
        plasticcode: "",
        productcode: "",
        addedservices: [],
        partnercard: "",
        clonecard: "",
        chargecard: "",
      },
      //Process
      process: {
        processname: defineProcessName(),
        stepname: defineStepName(),
        fielderror: "",
        version: "",
      },
      //Promotions
      promotions: {
        campaignid: defineCid(),
        wave: defineWave(),
        campaigncode: defineCampaignCode(),
      },
    };

    pushEvent({ event: "pageload", data: dataLayer });
  }, [
    i18n.resolvedLanguage,
    definePageName,
    defineStepName,
    defineSection_1,
    defineSection_2,
    defineAcquisitionPage,
    defineInternalCampaign,
    defineMedium,
    defineProcessName,
    pushEvent,
    defineCampaignCode,
    defineWave,
    defineCid,
  ]);

  const fireInitialization = useCallback(() => {
    let dataLayer: PageLoad = {
      //Page
      page: {
        language: i18n.resolvedLanguage ?? "en",
        pagename: "",
        currentpagename: "",
        section1: defineSection_1(),
        section2: defineSection_2(),
        acquisitionpage: defineAcquisitionPage(),
        intcmp: defineInternalCampaign(),
        medium: defineMedium(),
      },
      //Product
      product: {
        sourcecode: "",
        plasticcode: "",
        productcode: "",
        addedservices: [],
        partnercard: "",
        clonecard: "",
        chargecard: "",
      },
      //Process
      process: {
        processname: defineProcessName(),
        stepname: "",
        fielderror: "",
        version: "",
      },
      //Promotions
      promotions: {
        campaignid: defineCid(),
        wave: defineWave(),
        campaigncode: defineCampaignCode(),
      },
    };

    pushEvent({ event: "initialization", data: dataLayer });
  }, [
    defineAcquisitionPage,
    defineSection_1,
    defineSection_2,
    i18n.resolvedLanguage,
    pushEvent,
    defineInternalCampaign,
    defineMedium,
    defineProcessName,
    defineCampaignCode,
    defineWave,
    defineCid,
  ]);

  const fireFieldError = useCallback(() => {
    let dataLayer: PageLoad = {
      //Process
      process: {
        fielderror: "reCaptcha",
      },
    };
    pushEvent({ event: "fielderror", data: dataLayer });
  }, [pushEvent]);

  return { loadAdobeScript, firePageLoad, fireInitialization, fireFieldError };
};
